import { boolFromUnknown, intFromUnknown, strFromUnknown } from '@/lib/primitive-utils';
import { AcceptENotationCheckbox, AcceptScientificNotationCheckbox, HideENotationCheckbox } from './shared/scientific-notation-fields';
import { SignificantFiguresControls, sigFigUiModeFromProps } from './shared/significant-figures-controls';
import { AbsoluteValueCheckbox, ToleranceField, WidthField } from './shared/tolerance-width-fields';
import type { BuilderPropertyRecord, ShortAnswerToolSettingsProps } from './tool-defaults';

export function selectSymbolicNumberToolProps(p: BuilderPropertyRecord) {
    const requireSigFig = boolFromUnknown(p.require_sig_fig);
    const requireN = intFromUnknown(p.require_n_or_fewer_sig_figs, 0);
    return {
        sigFigMode: sigFigUiModeFromProps({ require_sig_fig: requireSigFig, require_n_or_fewer_sig_figs: requireN }),
        requireNOrFewer: requireN,
        acceptScientificNotation: boolFromUnknown(p.accept_scientific_notation),
        acceptENotation: boolFromUnknown(p.accept_E_notation),
        hideENotation: boolFromUnknown(p.hide_E_notation),
        tolerance: strFromUnknown(p.tolerance, '3%'),
        absoluteValue: boolFromUnknown(p.absolute_value),
        width: intFromUnknown(p.width, 150),
    };
}

function SymbolicNumberToolSettingsInner(
    props: ReturnType<typeof selectSymbolicNumberToolProps> & {
        onPatch: (patch: BuilderPropertyRecord) => void;
    },
) {
    const {
        sigFigMode,
        requireNOrFewer,
        acceptScientificNotation,
        acceptENotation,
        hideENotation,
        tolerance,
        absoluteValue,
        width,
        onPatch,
    } = props;

    return (
        <div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
                <SignificantFiguresControls
                    mode={sigFigMode}
                    requireNOrFewer={requireNOrFewer}
                    onPatch={onPatch}
                />
                <ToleranceField
                    tolerance={tolerance}
                    onPatch={onPatch}
                />

                <WidthField
                    width={width}
                    onPatch={onPatch}
                />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
                <AcceptScientificNotationCheckbox
                    value={acceptScientificNotation}
                    onPatch={onPatch}
                />
                <AbsoluteValueCheckbox
                    absoluteValue={absoluteValue}
                    onPatch={onPatch}
                />
                <AcceptENotationCheckbox
                    value={acceptENotation}
                    onPatch={onPatch}
                />
                {acceptENotation && (
                    <HideENotationCheckbox
                        value={hideENotation}
                        onPatch={onPatch}
                    />
                )}
            </div>
        </div>
    );
}

export function SymbolicNumberToolSettings({ builderProps, onPatch }: ShortAnswerToolSettingsProps) {
    return (
        <SymbolicNumberToolSettingsInner
            {...selectSymbolicNumberToolProps(builderProps)}
            onPatch={onPatch}
        />
    );
}
